import { useEffect, useState } from "react";

/**
 * Renders a hero background (image or video) with a dark overlay so foreground
 * text (text-primary-foreground) stays readable.
 *
 * Includes a subtle scroll parallax on the media plus an animated diagonal
 * "sheen" sweep so every hero across the site feels alive and cinematic.
 *
 * Place inside a `relative` parent. Children of the parent should be
 * wrapped in a `relative` container of their own to render above the media.
 */
export function HeroBackground({
  src,
  alt,
  video,
  poster,
  overlay = "black-soft",
  parallax = true,
  sheen = true,
}: {
  src: string;
  alt: string;
  video?: string;
  poster?: string;
  /** "heavy" = dark blue for white text over busy media; "soft" = lighter blue so the image reads through; "black-soft" = subtle black tint that keeps the photo crisp. */
  overlay?: "heavy" | "soft" | "black-soft";
  parallax?: boolean;
  sheen?: boolean;
}) {
  const [scrollY, setScrollY] = useState(0);
  useEffect(() => {
    if (!parallax) return;
    const onScroll = () => setScrollY(window.scrollY);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [parallax]);
  const translate = parallax ? Math.min(scrollY * 0.25, 220) : 0;

  return (
    <>
      <div
        className="absolute inset-0 will-change-transform"
        style={{ transform: `translate3d(0, ${translate}px, 0) scale(1.08)` }}
      >
        {video ? (
          <video
            src={video}
            poster={poster ?? src}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-label={alt}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <img
            src={src}
            alt={alt}
            width={1920}
            height={1080}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </div>
      {overlay === "heavy" ? (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/75 to-primary/85" />
      ) : overlay === "black-soft" ? (
        <div className="absolute inset-0 bg-black/35 md:bg-gradient-to-r md:from-black/45 md:via-black/25 md:to-transparent" />
      ) : (
        <div className="absolute inset-0 bg-primary/60 md:bg-gradient-to-r md:from-primary/65 md:via-primary/40 md:to-primary/20" />
      )}
      {sheen ? (
        <div className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-screen [background-image:linear-gradient(115deg,transparent_0%,transparent_45%,hsl(var(--secondary)/0.55)_50%,transparent_55%,transparent_100%)] [background-size:200%_100%] animate-[heroSheen_9s_ease-in-out_infinite]" />
      ) : null}
    </>
  );
}
